import { Col, Row } from "reactstrap";

//App
import { LookupGroupProps } from "../../../../types/lookupGroup";
import useComponentGeneralStyles from "../../../styles";
import useLookupGroup from "./hooks/useLookupGroup";
import { FormikTypeaheadOption, SearchProps } from "../../../../types";
import { LookupGroupFormContainer } from "./LookupGroupFormContainer";
import { LookupGroupDto } from "../../../../app/model/api/types/dto";
import { SearchDefaultValue } from "../../../../store/types";
import { SearchFieldsDefaultValue } from "../../../../utility/constants";
import LookupGroupView from "./LookupGroupView";

//Common
import { MoreActionButton } from "../../../common/Buttons";
import PageHeader from "../../../common/PageHeader/PageHeader";
import Drawer from "../../../common/VFX-UI/Drawer/Drawer";
import Snackbar from "../../../common/VFX-UI/SnackBar/Snackbar";
import ConfirmationDialog from "../../../common/VFX-UI/ConfirmationDialog/ConfirmationDialog";
import MoreActionsMenu from "../../../common/MoreActionsMenu/MoreActionsMenu";
import SearchContainer from "../../../common/Forms/SearchContainer/SearchContainer";
import { Chip, DataGrid } from "../../../common/VFX-UI";
import LookupValueGrid from "./LookupValueGrid";

const LookupGroup: React.FC<LookupGroupProps> = (props) => {
    const { data: lookupGroups, activateMutation, deleteMutation } = props;
    const generalStyles = useComponentGeneralStyles();

    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleDeactivateHandler,
        toggleActivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useLookupGroup({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    const handleOnLookupTypeChange = async (value: FormikTypeaheadOption[]) => {
        let lookupTypeId: string | null = null;

        if (value && value?.length > 0) {
            lookupTypeId = (value[0] as Record<string, string | number>)[
                "value"
            ] as string;
        }
        await formContext?.setLookupTypeId(lookupTypeId);
    };

    const searchProps: SearchProps<LookupGroupDto> = {
        dropdown: {options: formContext?.lookupTypes ?? [], defaultSelected: formContext?.lookupTypeId != null ? [formContext.lookupTypeId] : [], 
            clearButton: true, name: "lookupTypeId", label: "Category", onChange: (value:any) => handleOnLookupTypeChange(value) },
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: { ...SearchFieldsDefaultValue, name: true },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };

    const columns = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },
        {
            dataField: "description",
            text: "Description",
            sort: true,
        },
        {
            dataField: "lookupType",
            text: "Category",
            sort: true,
        },
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean) => {
                return <Chip label={cell ? "Active" : "Inactive"} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ];

    const expandRow = (row: LookupGroupDto) => {
        return <LookupValueGrid lookupGroupId={row.id ?? null} />;
    };

    return (
        <>
            <PageHeader>Lookup Group</PageHeader>
            <div style={generalStyles.searchContainer}>
                <Row style={generalStyles.width100}>
                    <Col sm={12}>
                        <SearchContainer
                            searchProps={searchProps}
                            activeToggle={formContext?.activeToggle ?? true}
                            handleActiveToggleChange={handleActiveToggleChange}
                            toggleNewViewHandler={
                                formContext?.lookupTypeId !== null
                                    ? toggleNewViewHandler
                                    : undefined
                            }
                        />
                    </Col>
                </Row>
            </div>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <LookupGroupFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <LookupGroupFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <LookupGroupView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <DataGrid
                columns={columns}
                data={lookupGroups ?? []}
                keyField="id"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                remote={true}
                pageSize={formContext?.pageSize}
                page={formContext?.paging?.currentPage}
                handlePageChange={async (newPage, pageSize ,sortField, sortOrder) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    if (formContext?.setSortBy && sortField && sortOrder) {
                        await formContext?.setSortBy({
                            field: sortField,
                            direction: sortOrder === "asc" ? 1 : -1,
                        });
                    }
                    formContext?.refetchGrid();
                }}
                isExpandableRow={true}
                expandRowData={expandRow}
            />

            {actionColumnAnchorEl && (
                <MoreActionsMenu
                    isOpen={openMenu}
                    target={actionColumnAnchorEl}
                    toggle={() => {
                        setActionColumnAnchorEl(null);
                    }}
                    onViewClick={() => {
                        toggleViewHandler();
                        setActionColumnAnchorEl(null);
                    }}
                />
            )}

            <ConfirmationDialog
                open={state.isDeactivateDialogOpen}
                onClose={toggleDeactivateHandler}
                onConfirm={handleDeactivate}
                contentTitle={`Deactivate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>

            <ConfirmationDialog
                open={state.isActivateDialogOpen}
                onClose={toggleActivateHandler}
                onConfirm={handleActivate}
                contentTitle={`Activate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            />

            {state.showSuccessSnackbar && (
                <Snackbar
                    open={state.showSuccessSnackbar}
                    message={state.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default LookupGroup;
